import { App, Button } from 'antd';
import { useFormikContext } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ClientFormValues } from '../models/Forms';
import { isIdentityDocumentValidationMarket } from '../market/resolveIdentityDocumentValidationCountry';
import { I18nNameSpaces } from '../services/i18n';

import { resolveIdentityDocumentRegistryFeedback } from './documentRegistryFeedbackState';
import { shouldRunIdentityDocumentValidation } from './identityDocumentFingerprint';
import { isIdentificationNumberValidForRegistryCheck } from './isIdentificationNumberValidForRegistryCheck';
import { runClientIdentityDocumentValidation } from './runClientIdentityDocumentValidation';

export function IdentityDocumentRegistryManualCheckButton() {
  const formik = useFormikContext<ClientFormValues>();
  const { message } = App.useApp();
  const { t } = useTranslation(I18nNameSpaces.Forms);
  const [isPending, setIsPending] = useState(false);

  const canRunRegistryCheck =
    isIdentityDocumentValidationMarket() && shouldRunIdentityDocumentValidation(formik.values);

  if (!canRunRegistryCheck) {
    return null;
  }

  const hasRegistryFeedback = resolveIdentityDocumentRegistryFeedback(formik.values) !== null;
  const labelKey = hasRegistryFeedback
    ? 'clientForm.identityDocumentValidation.manualRecheck'
    : 'clientForm.identityDocumentValidation.manualCheck';

  async function handleClick() {
    const identificationNumberValid = await isIdentificationNumberValidForRegistryCheck(formik);
    if (!identificationNumberValid) {
      return;
    }

    setIsPending(true);
    try {
      const outcome = await runClientIdentityDocumentValidation(formik.values, {
        formikRef: { current: formik },
      });

      if (outcome.type === 'unavailable') {
        void message.warning(t('clientForm.identityDocumentValidation.registryUnavailable'));
      }
    } finally {
      setIsPending(false);
    }
  }

  return (
    <Button
      type="default"
      className="identity-document-registry-manual-check"
      loading={isPending}
      disabled={isPending}
      onClick={() => {
        void handleClick();
      }}
    >
      {t(labelKey)}
    </Button>
  );
}
